import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { Music, X, Loader2, CheckCircle, Play, Pause, Download, Eye, RefreshCw, MicOff } from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { useWorkflowStore } from '../../store/workflowStore';
import { useNodeExecution } from '../../hooks/useNodeExecution';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import NodeError from './NodeError';
import NodeResetButton from './NodeResetButton';
import {
  ReconfigureButton, ReconfigureNotice, useReconfigure,
} from './ReconfigureControls';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import SendToTimelineButton from '../SendToTimelineButton';
import DirectorsTrigger from '../studio/DirectorsTrigger';
import PromptField from '../studio/PromptField';
import NodeRequirements from './NodeRequirements';
import { missingOf, requirementsTitle, textNeed } from '../../utils/nodeRequirements';

const OUTPUT_FORMATS = [
  { value: 'mp3_standard', label: 'MP3 128k (estándar)' },
  { value: 'mp3_high_quality', label: 'MP3 192k (alta calidad)' },
  { value: 'wav_16khz', label: 'WAV 16kHz (voz)' },
  { value: 'wav_22khz', label: 'WAV 22kHz' },
  { value: 'wav_24khz', label: 'WAV 24kHz' },
  { value: 'wav_cd_quality', label: 'WAV 44.1kHz (CD, sin compresión)' },
];

const formatLength = (sec) => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}:${s.toString().padStart(2, '0')}` : `${s}s`;
};

const MusicProNode = memo(({ id, data, selected }) => {
  const [chronometer, setChronometer] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [prompt, setPrompt] = useState(data.prompt || '');
  // La UI trabaja en segundos; el MCP/manifest escribe milisegundos (data.musicLengthMs).
  const [lengthSec, setLengthSec] = useState(
    data.musicLengthMs ? Math.round(data.musicLengthMs / 1000) : (data.lengthSec ?? 10)
  );
  // El modelo default es instrumental (force_instrumental=true en el schema).
  const [forceInstrumental, setForceInstrumental] = useState(data.forceInstrumental ?? true);
  const [outputFormat, setOutputFormat] = useState(data.outputFormat || 'mp3_standard');
  const [lastPrompt, setLastPrompt] = useState(data.lastPrompt || '');

  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const addAsset = useWorkflowStore((state) => state.addAsset);

  const { processNode, status: execStatus, error: execError, reset: resetExec, insufficientCredits } = useNodeExecution();
  const { openAudioPreview } = useMediaPreview();

  const isProcessing = execStatus === 'uploading' || execStatus === 'queued' || execStatus === 'processing';

  useEffect(() => {
    updateNodeData(id, { prompt, lengthSec, musicLengthMs: Math.round(lengthSec * 1000), forceInstrumental, outputFormat });
  }, [prompt, lengthSec, forceInstrumental, outputFormat]); // eslint-disable-line

  useEffect(() => {
    let interval;
    if (isProcessing) {
      setChronometer(0);
      interval = setInterval(() => setChronometer((prev) => prev + 1), 1000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [isProcessing]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlay = useCallback(() => {
    const audio = document.getElementById(`musicpro-audio-${id}`);
    if (!audio) return;
    if (isPlaying) audio.pause(); else audio.play();
    setIsPlaying(!isPlaying);
  }, [id, isPlaying]);

  // Regenerar descarta el resultado; volver a configurar lo conserva.
  const { reconfigure, setReconfigure, showForm } = useReconfigure(data);

  const handleProcess = useCallback(async () => {
    const usedPrompt = prompt.trim();
    if (!usedPrompt) return false;
    resetExec();

    const result = await processNode({
      resultType: 'music_pro',
      files: [],
      params: {
        prompt: usedPrompt,
        music_length_ms: Math.round(lengthSec * 1000),
        force_instrumental: forceInstrumental,
        output_format: outputFormat,
      },
    });

    if (result?.outputUrl) {
      setLastPrompt(usedPrompt);
      setReconfigure(false);
      const ext = outputFormat.startsWith('wav') ? 'wav' : 'mp3';
      updateNodeData(id, {
        hasOutput: true,
        outputUrl: result.outputUrl,
        assetId: result.assetId ?? null,
        outputType: 'audio',
        lastPrompt: usedPrompt,
        processedAt: new Date().toISOString(),
      });
      addAsset({
        id: `asset_${Date.now()}`,
        type: 'audio',
        tool: 'musicPro',
        filename: `musicpro_${Date.now()}.${ext}`,
        url: result.outputUrl,
        prompt: usedPrompt,
        createdAt: new Date().toISOString(),
      });
      return true;
    }
    return false;
  }, [id, prompt, lengthSec, forceInstrumental, outputFormat,
      updateNodeData, addAsset, processNode, resetExec, setReconfigure]);

  const handleRegenerate = useCallback(() => {
    updateNodeData(id, { hasOutput: false, outputUrl: null });
    resetExec();
  }, [id, updateNodeData, resetExec]);

  const { isInQueue, isMyTurn } = useWorkflowTrigger(id, handleProcess);

  const canProcess = prompt.trim().length > 0;

  // NR-1 · qué le falta al nodo para generar (se muestra debajo del botón)
  const runMissing = missingOf([textNeed(prompt)]);

  return (
    <div className={`node-container musicPro-node ${selected ? 'selected' : ''} ${isInQueue ? 'workflow-queued' : ''} ${isMyTurn ? 'workflow-active' : ''}`} data-testid="musicpro-node">
      <NodeResetButton nodeId={id} hasOutput={!!data.hasOutput} />

      {/* Header */}
      <div className="node-header musicPro-header">
        <div className="node-header-left">
          <Music size={16} />
          <span>Music Pro</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)} data-testid="delete-musicpro">
          <X size={14} />
        </button>
      </div>

      {/* Content */}
      <div className="node-content musicPro-content">
        {isProcessing ? (
          <div className="node-processing">
            <div className="node-chronometer">{formatTime(chronometer)}</div>
            <div className="node-processing-info">
              <Loader2 size={16} className="animate-spin" />
              <span>Componiendo música...</span>
            </div>
            <p className="node-processing-hint">{formatLength(lengthSec)} · {forceInstrumental ? 'instrumental' : 'con voces'}</p>
          </div>
        ) : !showForm ? (
          /* ===== RESULT VIEW ===== */
          <div className="node-result qwen3tts-result">
            <audio
              id={`musicpro-audio-${id}`}
              src={data.outputUrl}
              onEnded={() => setIsPlaying(false)}
            />
            <div className="qwen3tts-player">
              <button className="qwen3tts-play-btn" onClick={togglePlay} data-testid="musicpro-play-btn">
                {isPlaying ? <Pause size={24} /> : <Play size={24} />}
              </button>
              <div className="qwen3tts-waveform">
                {[...Array(12)].map((_, i) => (
                  <div
                    key={`wave-${i}`}
                    className={`qwen3tts-wave-bar ${isPlaying ? 'animate' : ''}`}
                    style={{ animationDelay: `${i * 0.1}s` }}
                  />
                ))}
              </div>
            </div>
            <div className="node-result-actions">
              <button className="node-mini-preview-btn" onClick={() => openAudioPreview(data.outputUrl, 'Music Pro')} data-testid="musicpro-preview-btn">
                <Eye size={12} /> Ver
              </button>
              <button className="node-mini-download-btn" onClick={(e) => { e.stopPropagation(); downloadFile(data.outputUrl, `musicpro_${Date.now()}.${outputFormat.startsWith('wav') ? 'wav' : 'mp3'}`); }} data-testid="musicpro-download-btn">
                <Download size={12} /> Descargar
              </button>
              <SendToTimelineButton assetId={data.assetId} nodeId={id} url={data.outputUrl} kind="audio" label="musicPro" testIdPrefix="musicpro-timeline" />
            </div>
            <div className="nano-regen-section">
              <span className="nano-section-label">Prompt usado:</span>
              <PromptField
                value={prompt}
                onChange={setPrompt}
                nodeId={id}
                nodeType="musicPro"
                label="Prompt de regeneración"
                className="nano-prompt-textarea nano-regen-textarea"
                rows={2}
                testId="musicpro-regen-prompt"
              />
              <button className="nano-regen-btn musicPro-regen-btn" onClick={handleRegenerate} data-testid="musicpro-regen-btn">
                <RefreshCw size={12} />
                Regenerar
              </button>
              <ReconfigureButton onClick={() => setReconfigure(true)} testId="musicpro-reconfigure-btn" />
            </div>
            <div className="node-result-badge">
              <CheckCircle size={12} />
              <span>Música generada</span>
            </div>
          </div>
        ) : (
          <>
            {data.outputUrl && reconfigure && (
              <ReconfigureNotice
                previewUrl={null}
                onCancel={() => setReconfigure(false)}
                testId="musicpro-reconfigure-notice"
              />
            )}

            {/* Prompt */}
            <div className="nano-prompt-section">
              <div className="nano-prompt-header">
                <span className="nano-section-label">Prompt *</span>
                <DirectorsTrigger nodeId={id} nodeType="musicPro" currentPrompt={prompt} onInsert={setPrompt} />
              </div>
              <PromptField
                value={prompt}
                onChange={setPrompt}
                nodeId={id}
                nodeType="musicPro"
                placeholder="Género, instrumentos, mood, estructura (intro/estrofa/estribillo), con o sin voces..."
                className="nano-prompt-textarea"
                rows={3}
                testId="musicpro-prompt"
              />
            </div>

            {/* Duración */}
            <div className="eleV2-slider-row musicPro-length-row">
              <span className="nano-option-label">Duración</span>
              <input
                type="range"
                min={5}
                max={300}
                step={5}
                value={lengthSec}
                onChange={(e) => setLengthSec(parseInt(e.target.value, 10))}
                className="videoSound-slider"
                data-testid="musicpro-length"
              />
              <span className="videoSound-duration-value">{formatLength(lengthSec)}</span>
            </div>

            {/* Formato + instrumental */}
            <div className="nano-options-row musicPro-options">
              <div className="nano-option">
                <span className="nano-option-label">Formato</span>
                <select value={outputFormat} onChange={(e) => setOutputFormat(e.target.value)} className="nano-select" data-testid="musicpro-output-format">
                  {OUTPUT_FORMATS.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="banana2pro-toggles">
              <label className="banana2pro-toggle musicPro-toggle" title="Quita las voces: sólo música instrumental">
                <input type="checkbox" checked={forceInstrumental} onChange={(e) => setForceInstrumental(e.target.checked)} data-testid="musicpro-instrumental" />
                <MicOff size={11} />
                <span>Instrumental</span>
              </label>
            </div>

            <button
              className="node-process-btn musicPro-process-btn"
              onClick={handleProcess}
              disabled={!canProcess}
              title={requirementsTitle(runMissing)}
              data-testid="musicpro-process-btn"
            >
              <Play size={14} />
              {data.outputUrl && reconfigure ? 'Regenerar con cambios' : 'Componer música'}
            </button>
            <NodeRequirements missing={runMissing} />
          </>
        )}

        <NodeError error={execError} insufficientCredits={insufficientCredits} />
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle audio-handle"
        isConnectable={!!data.hasOutput}
      />
    </div>
  );
});

MusicProNode.displayName = 'MusicProNode';

export default MusicProNode;
